// Pezzi in comune tra la scheda (sotto 1024 px) e la tabella unica (da 1024 px) dei condomìni.
import type { ReactNode } from "react";
import { AlertTriangle, ChevronRight, FileText, Flame, Trash2, Zap } from "lucide-react";
import type { BollettaVista, Condominio, Contatore, Esito, OffertaMenu } from "@/api/preventivo";
import { COLORE_TONO, altriAddebiti, anno, euro, pcv, altriInFila, nomeBreve, quotaMenu, ricarico, ricaricoParti, spreadCalcolato, spreadMenu, spreadScritto, suFornitture, tono } from "@/lib/preventivoFormato";
import { BOLLETTA_TITOLO, VOCI_NON_LEGGIBILE } from "@/lib/analisiScritte";
import {
  FUORI_CONFRONTO, NON_LETTA, NOTA_DIFFERENZA, NOTA_RETE, OFFERTA_CONDOMINIO, RETE_COME_IN_BOLLETTA,
  GAS_NON_RIUSCITA, GAS_SENZA_CONFRONTO, SCEGLI_OFFERTA, SPREAD_CALCOLATO, SPREAD_NON_DICHIARATO, SPREAD_NON_LETTO, fornitureN, gasValoriMancanti,
  motivoDifferenza, nomeEliminaBolletta, tipoVenditore,
} from "./scritte";
import { scadutaIl } from "./scritteLista";
import { useModo } from "./modo";

/** «Dettaglio non leggibile»: la stessa frase dell'analisi in chat. */
export const DETTAGLIO_NON_LEGGIBILE = VOCI_NON_LEGGIBILE;

/** L'offerta del condominio, se scelta e ancora in vendita; `null` = «Scegli l'offerta». */
export function offertaDel(c: Condominio, offerte: OffertaMenu[]): OffertaMenu | null {
  return c.offerta_id === null ? null : offerte.find((o) => o.id === c.offerta_id) ?? null;
}

/** L'offerta che vale per un contatore: la sua, se ne ha una, altrimenti quella del condominio. Finché il condominio
 *  non ha un'offerta, nessun contatore ne ha (spec § 7.5). */
export function offertaDelContatore(k: Contatore, c: Condominio, offerte: OffertaMenu[]): OffertaMenu | null {
  if (offertaDel(c, offerte) === null) return null;
  const id = k.offerta_id ?? c.offerta_id;
  return offerte.find((o) => o.id === id) ?? null;
}

/** Differenza al mese di una bolletta con l'offerta del suo contatore; `null` se non c'è offerta o non si calcola. */
export function differenzaBolletta(b: BollettaVista, offerta: OffertaMenu | null): string | null {
  return offerta ? b.diff_mese_per_offerta[offerta.id] ?? null : null;
}

export function Importo({ x, className = "" }: { x: string | null; className?: string }) {
  return <span className={`tabular-nums ${COLORE_TONO[tono(x)]} ${className}`}>{euro(x)}</span>;
}

/** «Condominio Via Borgorose 15» e sotto «Duferco Energia · 8 forniture» (i nomi arrivano già
 *  con le iniziali maiuscole: `nomiDellaVista`, all'arrivo della vista). `inLinea`: tutto su una riga che va a capo, per la riga
 *  del condominio della tabella, con `children` in coda (il link dell'offerta, spec § 7). */
export function Intestazione({ c, inLinea = false, soloNome = false, children }: {
  c: Condominio; inLinea?: boolean; children?: ReactNode;
  /** Con almeno una bolletta gas (impostazione D, 26/10): il venditore e le forniture stanno nelle righe «Luce» e «Gas». */
  soloNome?: boolean;
}) {
  // spazio non separabile: il numero non va a capo senza il suo nome (revisione 2, N1)
  const forniture = c.n_forniture === 1 ? "1\u00a0fornitura" : `${c.n_forniture}\u00a0forniture`;
  // l'offerta attuale non si scrive più (spec § 8): nome, venditore, forniture
  const sotto = [c.venditore, forniture].filter(Boolean).join(" · ");
  const titolo = c.titolo;
  if (soloNome) {
    return inLinea
      ? <p className="min-w-0 break-words leading-snug"><span className="font-heading text-[15px] font-semibold text-text">{titolo}</span></p>
      : <h3 className="min-w-0 font-heading text-lg font-semibold leading-tight text-text break-words">{titolo}</h3>;
  }
  if (inLinea) {
    // riga del condominio della tabella (mockup «risparmio», variante 1): «Condominio Via Devoti · AGF Energy» nero e
    // grassetto, poi le forniture piccole e il link dell'offerta
    return (
      <p className="min-w-0 break-words leading-snug">
        <span className="font-heading text-[15px] font-semibold text-text">{titolo}</span>
        {c.venditore && (
          <>
            <span className="text-muted-foreground"> · </span>
            <span className="text-[15px] font-semibold text-text">{c.venditore}</span>
          </>
        )}
        <span className="ml-2 text-xs text-muted-foreground">{forniture}</span>
        {children}
      </p>
    );
  }
  return (
    <div className="min-w-0">
      <h3 className="font-heading text-lg font-semibold leading-tight text-text break-words">{titolo}</h3>
      <p className="mt-0.5 text-sm text-muted-foreground break-words">
        {sotto}
      </p>
    </div>
  );
}

/** «20 €/mese + 0,035 €/kWh» (come `nomeBreve`), ma la cifra e la sua unità non si separano: se non sta tutta, va a capo solo dopo il «+». */
export function OffertaBreve({ offerta }: { offerta: OffertaMenu }) {
  const scaduta = offerta.scaduta_il ? <> · <span className="whitespace-nowrap text-red-700">{scadutaIl(offerta.scaduta_il)}</span></> : null;
  if (offerta.quota === null || offerta.spread === null) return <>{offerta.nome}{scaduta}</>;
  return (
    <>
      <span className="whitespace-nowrap">{quotaMenu(offerta.quota)} +</span>{" "}
      <span className="whitespace-nowrap">{spreadMenu(offerta.spread)}</span>
      {scaduta}
    </>
  );
}

/** «nomeBreve», con «· scaduta il 2/11» se l'offerta scelta non è più in vendita (testo: non solo un colore). */
export function nomeConScadenza(o: OffertaMenu): string {
  return o.scaduta_il ? `${nomeBreve(o)} · ${scadutaIl(o.scaduta_il)}` : nomeBreve(o);
}

export const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

/** Il cestino di una bolletta (03/11): in fondo alla sua riga nella tabella, a destra in cima alla scheda sul telefono.
 *  Rosso come «Elimina preventivo»; apre la conferma. Si vede 32 px perché la sua colonna non tolga posto ad «Altri
 *  addebiti» a 1024 (misurato: con 48 px scendeva a 54), ma si tocca su 44 come la freccia degli addebiti. Il nome
 *  (lettore di schermo e fumetto) dice quale bolletta. */
export function EliminaBolletta({ b, onElimina }: { b: BollettaVista; onElimina: (b: BollettaVista) => void }) {
  return (
    <button
      type="button"
      aria-label={nomeEliminaBolletta(b.mese_label)}
      onClick={() => onElimina(b)}
      title={nomeEliminaBolletta(b.mese_label)}
      className={`relative inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-700 cursor-pointer touch-manipulation hover:bg-red-50 after:absolute after:-inset-1.5 after:content-[''] ${FOCUS}`}
    >
      <Trash2 size={18} aria-hidden="true" />
    </button>
  );
}

/** Riquadro «Offerta del condominio · 20 €/mese + 0,035 €/kWh · −4,98 € al mese · Cambia ›», o «Scegli l'offerta» finché non ce
 *  n'è una. `mese`: la differenza al mese del condominio, sotto il nome dell'offerta. */
export function RiquadroOfferta({ offerta, mese = null, onCambia }: { offerta: OffertaMenu | null; mese?: string | null; onCambia: () => void }) {
  // Il pulsante prende il focus prima di aprire il pannello: Safari non lo dà al tocco, e il pannello chiudendosi lo
  // rimette su chi l'ha aperto.
  const apri = (e: { currentTarget: HTMLElement }) => { e.currentTarget.focus(); onCambia(); };
  const { solaLettura } = useModo();
  // copia per l'amministratore: l'offerta e la cifra, senza «Cambia» (niente da scegliere)
  if (solaLettura) {
    if (offerta === null) return null;
    return (
      <div className="min-h-12 rounded-lg border border-border bg-surface px-3 py-2.5">
        <span className="block text-xs text-muted-foreground">{OFFERTA_CONDOMINIO}</span>
        <span className="block font-semibold text-text break-words"><OffertaBreve offerta={offerta} /></span>
        {mese !== null && (
          <span className="block text-sm font-semibold"><Importo x={mese} /> <span className="font-medium text-text">al mese</span></span>
        )}
      </div>
    );
  }
  if (offerta === null) {
    return (
      <button
        type="button"
        onClick={apri}
        className={`inline-flex h-12 w-full items-center justify-center gap-1 rounded-lg bg-brand-dark px-4 text-base font-semibold text-white cursor-pointer hover:bg-dark transition-colors ${FOCUS} focus-visible:ring-offset-2`}
      >
        {SCEGLI_OFFERTA}
        <ChevronRight size={18} aria-hidden="true" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={apri}
      className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left cursor-pointer hover:bg-border/60 transition-colors ${FOCUS}`}
    >
      <span className="min-w-0">
        <span className="block text-xs text-muted-foreground">{OFFERTA_CONDOMINIO}</span>
        <span className="block font-semibold text-text break-words"><OffertaBreve offerta={offerta} /></span>
        {mese !== null && (
          <span className="block text-sm font-semibold"><Importo x={mese} /> <span className="font-medium text-text">al mese</span></span>
        )}
      </span>
      <span className="inline-flex shrink-0 items-center gap-0.5 text-sm font-medium text-brand-dark">
        Cambia
        <ChevronRight size={16} aria-hidden="true" />
      </span>
    </button>
  );
}

/** «−28,57 € al mese», «−343 € l'anno, a consumi uguali», «su 6 delle 8 forniture». Niente se non c'è offerta. */
export function Risultato({ c, offerta, grande = true }: { c: Condominio; offerta: OffertaMenu | null; grande?: boolean }) {
  if (offerta === null || c.mese === null) return null;
  return (
    <div>
      <p className={`font-heading font-semibold leading-none ${grande ? "text-3xl" : "text-2xl"}`}>
        <Importo x={c.mese} />
        <span className="font-sans text-base font-medium text-text"> al mese</span>
      </p>
      <NoteRisultato c={c} offerta={offerta} className="mt-1" />
    </div>
  );
}

/** Le righe sotto la cifra: stima all'anno e «su 6 delle 8 forniture». Sulla scheda stanno sotto il pulsante
 *  dell'offerta, che porta già la cifra al mese. */
export function NoteRisultato({ c, offerta, className = "", testo = "text-sm" }: { c: Condominio; offerta: OffertaMenu | null; className?: string; testo?: string }) {
  if (offerta === null || c.mese === null) return null;
  const parziale = suFornitture(c.contatori_contati, c.contatori_totali);
  if (c.anno === null && !parziale) return null;
  return (
    <div className={`space-y-0.5 ${testo} text-muted-foreground ${className}`}>
      {c.anno !== null && <p>{anno(c.anno)}</p>}
      {parziale && <p>{parziale}</p>}
    </div>
  );
}

/** Il mese della bolletta: apre il file caricato. Alto 44 px anche dentro una riga di testo (bersaglio tattile).
 *  Accanto, l'icona della bolletta che apre lo stesso file (ritocchi 24/10), con l'area di tocco del link del mese:
 *  alta 44 px, larga quanto l'icona; le due aree si toccano e aprono lo stesso file. Stretta perché la colonna del
 *  mese a 1024 px non ha posto per un quadrato di 44. */
export function MeseLink({ b, className = "", aCapo = false, compatta = false }: { b: BollettaVista; className?: string; aCapo?: boolean; compatta?: boolean }) {
  const { fileUrl } = useModo();
  if (b.mese_label === null) return <span className={className}>—</span>;
  // `aCapo` (tabella): il mese su una riga sola, anche doppio («Ago-Set 2026», giro 4 del link), dai 1280 px; sotto, nella
  // pagina dell'operatore a 1024 (con la barra laterale) la colonna che servirebbe toglieva ad «Altri addebiti» quasi
  // tutto il suo posto (misurato: 102 → 82 px con «Ago-Set», 48 con «Dic-Gen»), quindi lì va ancora a capo dopo il
  // trattino
  // `compatta` (tabella fra 640 e 1023 px): il mese sta sopra la fornitura, su una riga sola e alto 24 px; l'area di tocco
  // di 44 px sporge sopra e sotto senza alzare la riga
  const pezzi = aCapo || compatta ? b.mese_label.split(/(?<=-)/) : [b.mese_label];
  const nowrap = aCapo && !compatta ? "whitespace-nowrap" : "";
  const alto = compatta ? "relative min-h-6 after:absolute after:-inset-y-2.5 after:inset-x-0 after:content-['']" : "min-h-11";
  // compatta: il mese doppio («Dic 2025-Gen 2026») va a capo dopo il trattino solo se la colonna è troppo stretta
  const testo = compatta && pezzi.length > 1
    ? <span>{pezzi.map((p, i) => <span key={i} className="whitespace-nowrap">{i > 0 && <wbr />}{p}</span>)}</span>
    : pezzi.length > 1
    ? <span className="whitespace-nowrap">{pezzi[0]}<br className="xl:hidden" />{pezzi[1]}</span>
    : b.mese_label;
  const file = fileUrl(b.id);
  // copia per l'amministratore senza il file di questa bolletta: il mese da leggere, senza link né icona
  if (file === null) {
    return <span className={`inline-flex ${compatta ? "min-h-6" : "min-h-11"} items-center ${nowrap} ${className}`}>{testo}</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <a
        href={file}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex ${alto} items-center text-brand-dark underline underline-offset-2 hover:text-brand rounded-sm ${FOCUS} ${nowrap}`}
      >
        {testo}
      </a>
      <a
        href={file}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={BOLLETTA_TITOLO}
        title={BOLLETTA_TITOLO}
        className={`inline-flex ${alto} shrink-0 items-center px-0.5 rounded-sm text-brand-dark hover:text-brand ${FOCUS}`}
      >
        <FileText size={16} aria-hidden="true" />
      </a>
    </span>
  );
}

const SOTTO = "block text-xs font-normal text-muted-foreground";

export function Pcv({ b }: { b: BollettaVista }) {
  return <>{b.pcv === null ? "—" : pcv(b.pcv)}</>;
}

/** Le parole sotto lo spread che non è stampato: «non letto», «non dichiarato», o «non dichiarato, calcolato dalla
 *  bolletta» se il conto ha un valore; `null` a prezzo fisso (niente da dire). */
export function notaSpreadMancante(b: BollettaVista): string | null {
  if (b.spread_stato === "non_letto") return SPREAD_NON_LETTO;
  if (b.spread_stato !== "non_dichiarato") return null;
  return b.spread_calcolato ? SPREAD_CALCOLATO : SPREAD_NON_DICHIARATO;
}

/** «0,03», con sotto la dicitura stampata se non è «spread» (tra «»), o la frase del motore che ha già le sue
 *  virgolette («la bolletta dice «comprensivo di perdite»: …»); se manca «—» con sotto lo stato, o «0,090» se
 *  il conto dà il valore dello spread non dichiarato. */
export function Spread({ b, senzaNote = false }: { b: BollettaVista; senzaNote?: boolean }) {
  const scritto = spreadScritto(b.spread);
  // `senzaNote` (tabella, variante A): solo il numero; la nota va nella riga sotto la bolletta
  if (scritto === null) {
    const nota = notaSpreadMancante(b);
    const principale = b.spread_stato === "non_dichiarato" && b.spread_calcolato ? spreadCalcolato(b.spread_calcolato) : "—";
    return senzaNote || nota === null ? <>{principale}</> : <>{principale}<span className={SOTTO}>{nota}</span></>;
  }
  if (senzaNote) return <>{scritto}</>;
  const nota = b.spread_nota && (b.spread_nota.includes("«") ? b.spread_nota : `«${b.spread_nota}»`);
  return <>{scritto}{nota && <span className={SOTTO}>{nota}</span>}</>;
}

/** «2,36 € (0,013 €/kWh)» e sotto dove sta; «—» se non c'è; niente se il conto non si è potuto fare (23/10: nessuna
 *  scritta che sembri un guasto). */
export function Ricarico({ b, senzaNote = false, dueRighe = false }: { b: BollettaVista; senzaNote?: boolean; dueRighe?: boolean }) {
  if (b.ricarico === null) return b.ricarico_stato === "non_calcolabile" ? null : <>—</>;
  // tabella compatta: l'importo sopra, il prezzo al kWh sotto, più piccolo (stesse parole della riga unica)
  if (dueRighe) {
    const [importo, perKwh] = ricaricoParti(b.ricarico, b.ricarico_kwh, b.ricarico_minimo);
    return <><span className="block">{importo}</span>{perKwh && <span className={SOTTO}>{perKwh}</span>}</>;
  }
  return <>{ricarico(b.ricarico, b.ricarico_kwh, b.ricarico_minimo)}{!senzaNote && b.ricarico_dove && <span className={SOTTO}>{b.ricarico_dove}</span>}</>;
}

/** «Accesso Portale Adempia 24,00 € · IVA 22%»; le spese per insoluto su una riga loro, con sotto «fuori dal
 *  confronto». */
export function Altri({ b, senzaNote = false }: { b: BollettaVista; senzaNote?: boolean }) {
  // tabella (variante A): tutte le voci in fila; «fuori dal confronto» va nella riga delle note. La percentuale resta con
  // la parola prima: a 1280 «IVA 22%» si spezzava e «22%» restava solo sulla seconda riga (autocritica 24/10)
  if (senzaNote) return <>{altriInFila(b.altri)}</>;
  const dentro = b.altri.filter((a) => !a.fuori_confronto);
  const fuori = b.altri.filter((a) => a.fuori_confronto);
  if (fuori.length === 0) return <>{altriAddebiti(dentro)}</>;
  return (
    <>
      {dentro.length > 0 && <span className="block">{altriAddebiti(dentro)}</span>}
      {fuori.map((a, i) => (
        <span key={i} className="block">{altriAddebiti([a])}<span className={SOTTO}>{FUORI_CONFRONTO}</span></span>
      ))}
    </>
  );
}

/** Una bolletta incompleta: dentro il condominio coi numeri letti, senza differenza (il server le dà `stato` «non_letta»
 *  e il dato che manca in `motivo`). Senza `motivo` è una bolletta non letta del tutto. */
export function eIncompleta(b: BollettaVista): boolean {
  return b.stato === "non_letta" && b.motivo !== null;
}

/** La frase che viene per prima nella riga delle note (tabella) e nel riquadro grigio (scheda): rete e oneri come in
 *  bolletta se la differenza c'è senza la potenza, o il dato che manca. `null` se non c'è nulla da dire (anche per
 *  `tipo_cliente_non_letto`, senza frase approvata). */
export function notaMotivo(b: BollettaVista): [etichetta: string, testo: string] | null {
  if (b.rete_dalla_bolletta) return [NOTA_RETE, RETE_COME_IN_BOLLETTA];
  const frase = motivoDifferenza(b.motivo);
  return eIncompleta(b) && frase ? [NOTA_DIFFERENZA, frase] : null;
}

/** Una bolletta del condominio non letta: lo stato in parole, con l'icona dell'elenco «Bollette caricate», al posto delle
 *  colonne d'analisi e della differenza (un «—» direbbe «non c'è», come se fosse letta). */
export function NonLetta({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-muted-foreground ${className}`}>
      <AlertTriangle size={16} aria-hidden="true" className="shrink-0 text-amber-700" />
      {NON_LETTA}
    </span>
  );
}

/** Seconda lettura in corso: un segnaposto, nessun testo (lo spazio resta, la pagina non salta). */
export function Segnaposto({ className = "" }: { className?: string }) {
  return (
    <span aria-busy="true" className={`block h-4 rounded bg-border/70 animate-pulse motion-reduce:animate-none ${className}`} />
  );
}

// --- il gas nel condominio (giro 1 del gas, impostazione D approvata il 26/10) ---

/** L'etichetta di un tipo di fornitura: icona (SVG, non emoji) e tipo scritto, col venditore; il colore del gas è l'ambra
 *  del mockup, ma il tipo è sempre scritto: il colore da solo non basta. `n`: quante forniture; `children` in coda
 *  (l'offerta scelta per la luce, «senza confronto con le offerte» per il gas). */
export function EtichettaTipo({ tipo, venditore, n, children }: { tipo: "Luce" | "Gas"; venditore: string | null; n: number; children?: ReactNode }) {
  const Icona = tipo === "Gas" ? Flame : Zap;
  return (
    <p className="min-w-0 break-words text-[13px] leading-snug">
      <span className={`inline-flex items-center gap-1.5 font-semibold ${tipo === "Gas" ? "text-[#8a5a12]" : "text-brand-dark"}`}>
        <Icona size={14} aria-hidden="true" className="shrink-0" />
        {tipoVenditore(tipo, venditore)}
      </span>
      <span className="ml-2 text-muted-foreground">{fornitureN(n)}</span>
      {/* telefono: su una riga sua, sotto l'icona allineata al testo; dal computer sulla stessa riga */}
      {tipo === "Gas" && <span className="ml-2 text-muted-foreground max-sm:ml-5 max-sm:mt-0.5 max-sm:block">{GAS_SENZA_CONFRONTO}</span>}
      {children}
    </p>
  );
}

/** Sotto la riga gas, se l'AI non ha controllato le voci: mancano i valori ufficiali del mese, o l'analisi non è riuscita.
 *  Mai le voci del motore a regole. `null` se l'analisi è fatta o ancora in corso. */
export function avvisoGas(b: BollettaVista): string | null {
  if (b.commodity !== "gas" || b.dettaglio !== "pronto") return null;
  if (b.analisi_gas === "valori_mancanti" && b.valori_del) return gasValoriMancanti(b.valori_del);
  return b.analisi_gas === "valori_mancanti" || b.analisi_gas === "non_riuscita" ? GAS_NON_RIUSCITA : null;
}

/** Gli esiti del gas con «Gas · venditore» dove la luce mette la sua fornitura (telefono: le voci gas dentro «Addebiti in più»). */
export function esitiGasDi(c: Condominio): Esito[] {
  const prefisso = tipoVenditore("Gas", c.venditore_gas ?? null);
  return (c.esiti_gas ?? []).map((e) => ({
    ...e, voci: e.voci.map((v) => ({ ...v, fornitura: [prefisso, v.fornitura].filter(Boolean).join(" · ") })),
  }));
}
